/**
 * Consolidated per-ref snapshot augmentation pass.
 *
 * One orchestrator that:
 *
 *   1. Parses the YAML once.
 *   2. Walks the tree once, building a per-ref `checks: Set<CheckId>`
 *      plan from each existing augmenter's role/text/inputValue
 *      filters.
 *   3. Resolves every needed ref once via `resolveRefsWithArgs` (the
 *      `aria-ref=` selector engine, NOT broken
 *      `[aria-ref="..."]` CSS), runs all applicable checks against
 *      each element inside one batched `evaluate`, returns a
 *      `PerRefResult`.
 *   4. Applies all attribute / role edits in a single yaml walk.
 *
 * Replaces the legacy chain
 *
 *   augmentFormFieldEditability →
 *   augmentGridCellValues →
 *   augmentFormFieldLabels →
 *   augmentFilterState →
 *   enrichModalDialogElementsV2
 *
 * with one call. Five of those used `document.querySelector(
 * '[aria-ref="${ref}"]')` — a silent no-op in production because
 * Playwright stores ref ids on `element._ariaRef` (isolated world).
 * The consolidated pass uses the correct path.
 *
 * Out of scope (separate mechanics, kept as-is):
 *   - Pre-snapshot DOM mutators (`enrichInteractiveSVGElements`,
 *     `enrichHiddenClickableElements`, `enrichModalDialogElements`).
 *   - Page-global one-shots (`augmentBlockingModalSignal`,
 *     `augmentHiddenSelectOptions`).
 *   - The multi-tier `augmentUnlabeledElements` (icon/generic/img/
 *     toggle/svgPromotion). Same fold pattern; deferred for a
 *     follow-up PR to keep this diff reviewable.
 *
 * @module
 */

import type { Frame, Page } from 'playwright';
import { errorMessage } from '../errors-internal.js';
import type { BrowserLogger } from '../logger.js';
import { encodeV2Value, parseSnapshot, type SnapshotElement } from '../snapshot-analyzer.js';
import { resolveIconClassName } from '../icon-class-names.js';
import {
  FORM_FIELD_ROLES,
  planPerRefChecks,
  type CheckId,
  type PerRefArgs,
  type PerRefAugmentationOpts,
} from './per-ref-augmentation-plan.js';
import { DEFAULT_REF_RESOLUTION_BATCH_SIZE, resolveRefsWithArgs } from './resolve-refs.js';
import { extractSemanticHint } from './semantic-hints.js';

export type { CheckId, PerRefAugmentationOpts } from './per-ref-augmentation-plan.js';

/**
 * Hint payload returned by the icon-button page-side body. All fields
 * optional — callers pick the strongest available signal.
 */
export interface IconHint {
  ariaLabel?: string;
  title?: string;
  ariaHaspopup?: string;
  ariaExpanded?: string;
  ariaControls?: string;
  ariaOwns?: string;
  ariaCurrent?: string;
  href?: string;
  pathname?: string;
  navAncestorLabel?: string;
  svgHint?: string;
  srText?: string;
  imgAlt?: string;
  reactComponentName?: string;
  id?: string;
  className?: string;
  lucideIconClass?: string;
  /** Raw icon-font class token from a child icon element (FontAwesome
   * `fa-file-export`, Telerik `k-svg-i-filter`, generic `icon-download`),
   * mapped to a friendly label by `resolveIconClassName` in the apply phase. */
  iconClass?: string;
}

/**
 * Hint payload for unlabeled clickable generics (e.g. `<div role="button">`).
 * Same shape as IconHint minus link-specific fields, plus `innerText` since
 * generics often carry visible text inside child spans the substrate stripped.
 */
export interface GenericHint {
  title?: string;
  ariaLabel?: string;
  svgHint?: string;
  id?: string;
  className?: string;
  innerText?: string;
  lucideIconClass?: string;
  /** Raw icon-font class token from a child icon element — see IconHint. */
  iconClass?: string;
}

/**
 * Hint payload for unlabeled clickable images. Only one alternative wins —
 * the page-side body returns the first valid label or `src` for downstream
 * src-anchored handling.
 */
export interface ClickableImgHint {
  alt?: string;
  title?: string;
  ariaLabel?: string;
  src?: string;
}

/**
 * Per-CheckId result shape. Indexed by CheckId so the union and the
 * result type cannot drift apart silently.
 */
export type PerRefResult = {
  editability?: { readonly: boolean; disabled: boolean } | null;
  gridCellValue?: string | null;
  formFieldLabel?: string | null;
  filterState?: { visible: number; queryInResults: boolean } | null;
  modalV2?: {
    augRole: 'dialog' | 'alertdialog';
    augName?: string;
    cssTag: string;
    cssClasses: string[];
    framework: boolean;
  } | null;
  iconButton?: IconHint | null;
  genericClickable?: GenericHint | null;
  clickableImg?: ClickableImgHint | null;
  toggle?: { label?: string; checked?: boolean; disabled?: boolean } | null;
  testId?: string | null;
  /** Stable-ish `data-id` entity attribute — carried so the locator can scope
   * a target under an identifying ancestor (after volatile-suffix stripping). */
  dataId?: string | null;
  dataField?: { field: string; editable: boolean } | null;
  svgPromote?: boolean;
  /** A `role=dialog`/`alertdialog` element's effective modality, verified
   * against the live DOM. `inert` is true when the dialog is not actually
   * rendered (zero box, fully off-screen, or `display:none`/`visibility:hidden`
   * on an ancestor) or has no visible interactive content — i.e. it should not
   * be treated as a blocking modal even if it carries `aria-modal=true`
   * (common with collapsed chat/announcer widgets). */
  dialogModality?: { inert: boolean } | null;
  /** Element is horizontally clipped inside a scroll container — agent must
   * scroll that direction to reach it. The genuinely useful, non-obvious
   * off-screen signal (vertical-below-fold is noise on any long list). */
  hclip?: 'left' | 'right' | null;
};

// Compile-time guard: every CheckId must have a corresponding optional
// field on PerRefResult. If a future CheckId is added without a result
// field, this line fails to typecheck.
type _CheckIdResultLink = Record<CheckId, unknown> & {
  [K in CheckId]: K extends keyof PerRefResult ? unknown : never;
};
const _checkIdGuard: _CheckIdResultLink = {} as PerRefResult & Record<CheckId, unknown>;
void _checkIdGuard;

// ─── Constants (shared, not per-ref) ────────────────────────────────────

const FILTER_FALLBACK_TEXT_RE_SOURCE =
  '^(browse|more|advanced|library|search\\s+all|load\\s+more|show\\s+all)\\b';
const RESULT_ITEM_SELECTOR =
  '[role=option],[role=listitem],[role=row],[data-slot=card],[data-slot=option],li';
const FILTER_QUERY_MIN_LEN = 3;
const FILTER_MAX_RESULT_ITEMS = 200;
const FILTER_ANCESTOR_WALK_DEPTH = 4;
const HCLIP_ANCESTOR_WALK_DEPTH = 10;
const MODAL_V2_MAX_ENRICHMENTS = 3;
const MODAL_V2_FRAMEWORK_SELECTORS = [
  '.cdk-overlay-pane',
  '.mat-dialog-container',
  '.mat-mdc-dialog-container',
  '.ui-dialog',
  '.modal-dialog',
  '.ant-modal-content',
  '.el-dialog',
  '.v-dialog',
  '.p-dialog',
];
const MODAL_V2_CLASS_PATTERNS = [
  '\\bmodal(?![-_]?(backdrop|mask|overlay|fade|bg|background))\\b',
  '\\bdialog\\b',
  '\\bpopup\\b',
  '\\blightbox\\b',
  '\\bdrawer\\b',
];

// Threshold for the "mass-null" warning: if >= this fraction of resolved
// refs return null from the page-side fn, log at warn — that's the
// fingerprint of the original broken-CSS bug we're guarding against.
const MASS_NULL_RATE = 0.5;
const LARGE_PASS_LOG_THRESHOLD = 512;

// ─── Public API ─────────────────────────────────────────────────────────

export interface V2ModalOverride {
  ref: string;
  augRole: 'dialog' | 'alertdialog';
  augName?: string;
  cssTag?: string;
  cssClasses?: string[];
}

export interface PerRefAugmentationResult {
  yaml: string;
  v2ModalOverrides: V2ModalOverride[];
}

function throwIfAborted(signal?: AbortSignal): void {
  if (!signal?.aborted) return;
  if (signal.reason !== undefined) throw signal.reason;
  const error = new Error('Per-ref augmentation aborted');
  error.name = 'AbortError';
  throw error;
}

// ─── Page-side dispatcher ───────────────────────────────────────────────

interface PageGlobals {
  RESULT_ITEM_SELECTOR: string;
  FILTER_FALLBACK_PATTERN: string;
  FILTER_MAX_ITEMS: number;
  FILTER_MIN_QUERY_LEN: number;
  FILTER_ANCESTOR_DEPTH: number;
  HCLIP_ANCESTOR_DEPTH: number;
  MODAL_FRAMEWORK_SELECTORS: string[];
  MODAL_CLASS_PATTERNS: string[];
}

/**
 * Browser-side dispatcher. Runs every requested check against the
 * resolved element. Standards-based signals only — no class/style
 * heuristics outside of the modalV2 framework-selector list (which
 * is preserved from the legacy v2 helper for behavior parity; the
 * standards-based modal-detection pass lives in
 * `augmentBlockingModalSignal`).
 *
 * The body must be self-contained — no closure variables; pass
 * everything via `args` (per-ref) or `globals` (shared) since the fn
 * is serialized as source and recompiled inside the page world.
 */
function pageSideFn(el: Element, _ref: string, args: PerRefArgs, G: PageGlobals): PerRefResult {
  const out: PerRefResult = {};
  const checks = new Set(args.checks);

  // ── editability ────────────────────────────────────────────────
  if (checks.has('editability')) {
    let readonly = false;
    let disabled = false;
    try {
      if (el.matches(':read-only')) readonly = true;
    } catch {
      /* shadow-DOM hosts can throw */
    }
    if (el.getAttribute('aria-readonly') === 'true') readonly = true;
    if (el.getAttribute('contenteditable') === 'false') readonly = true;
    if ((el as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement).disabled) {
      disabled = true;
    }
    if (el.getAttribute('aria-disabled') === 'true') disabled = true;
    // Disabled inputs match `:read-only` per spec — don't double-mark.
    // The agent already understands "disabled" as non-editable; an
    // additional `[readonly=true]` is redundant and noisy.
    if (disabled) readonly = false;
    out.editability = { readonly, disabled };
  }

  // ── hclip (horizontally clipped inside a scroll container) ─────
  if (checks.has('hclip')) {
    out.hclip = null;
    try {
      const win = el.ownerDocument.defaultView;
      if (win) {
        const elRect = el.getBoundingClientRect();
        if (elRect.width > 0 || elRect.height > 0) {
          let parent: Element | null = el.parentElement;
          for (
            let i = 0;
            i < G.HCLIP_ANCESTOR_DEPTH && parent;
            i++, parent = parent.parentElement
          ) {
            // Only horizontally-scrollable containers clip on the x-axis.
            if (parent.scrollWidth <= parent.clientWidth + 1) continue;
            const ox = win.getComputedStyle(parent).overflowX;
            if (ox !== 'auto' && ox !== 'scroll') continue;
            const cRect = parent.getBoundingClientRect();
            // The container's visible horizontal band, clipped to the viewport.
            const bandLeft = Math.max(cRect.left, 0);
            const bandRight = Math.min(cRect.right, win.innerWidth);
            if (elRect.right <= bandLeft) out.hclip = 'left';
            else if (elRect.left >= bandRight) out.hclip = 'right';
            break;
          }
        }
      }
    } catch {
      out.hclip = null;
    }
  }

  // ── gridCellValue ──────────────────────────────────────────────
  if (checks.has('gridCellValue')) {
    let value: string | null = null;
    const title = el.getAttribute('title');
    if (title) value = title;
    else if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
      value = el.value || null;
    } else {
      value = el.textContent?.trim() || null;
    }
    out.gridCellValue = value;
  }

  // ── formFieldLabel ─────────────────────────────────────────────
  if (checks.has('formFieldLabel')) {
    let label: string | null = null;
    if (
      el instanceof HTMLInputElement ||
      el instanceof HTMLTextAreaElement ||
      el instanceof HTMLSelectElement
    ) {
      const labels = el.labels;
      if (labels && labels.length > 0) {
        const t = labels[0].textContent?.trim();
        if (t) label = t;
      }
    }
    if (!label) {
      const precedingLabels: HTMLLabelElement[] = [];
      let prev = el.previousElementSibling;
      while (prev) {
        if (prev.tagName === 'LABEL') {
          precedingLabels.unshift(prev as HTMLLabelElement);
        } else break;
        prev = prev.previousElementSibling;
      }
      if (precedingLabels.length > 1) {
        const selected = precedingLabels.find((l) => {
          const cls = l.className.toLowerCase();
          return (
            cls.includes('selected') ||
            cls.includes('active') ||
            cls.includes('current') ||
            cls.includes('checked')
          );
        });
        const t = (selected ?? precedingLabels[0])?.textContent?.trim();
        if (t) label = t;
      } else if (precedingLabels.length === 1) {
        const t = precedingLabels[0].textContent?.trim();
        if (t) label = t;
      }
    }
    if (!label) {
      const labelledBy = el.getAttribute('aria-labelledby');
      if (labelledBy) {
        const target = document.getElementById(labelledBy);
        const t = target?.textContent?.trim();
        if (t) label = t;
      }
    }
    out.formFieldLabel = label;
  }

  // ── filterState ────────────────────────────────────────────────
  if (checks.has('filterState')) {
    const value = args.searchValue ?? '';
    const fallbackRe = new RegExp(G.FILTER_FALLBACK_PATTERN, 'i');

    const findContainer = (input: Element): Element | null => {
      let parent: Element | null = input.parentElement;
      for (let i = 0; i < G.FILTER_ANCESTOR_DEPTH && parent; i++, parent = parent.parentElement) {
        const role = parent.getAttribute('role');
        const tag = parent.tagName.toLowerCase();
        if (
          role === 'region' ||
          role === 'search' ||
          role === 'group' ||
          role === 'listbox' ||
          tag === 'section' ||
          tag === 'form'
        )
          return parent;
        const style = parent.ownerDocument.defaultView?.getComputedStyle(parent);
        if (
          style &&
          (style.overflowY === 'auto' ||
            style.overflowY === 'scroll' ||
            style.overflow === 'auto' ||
            style.overflow === 'scroll')
        )
          return parent;
      }
      return null;
    };
    const isVisible = (n: Element): boolean => {
      const rect = (n as HTMLElement).getBoundingClientRect?.();
      if (!rect) return true;
      if (rect.width === 0 && rect.height === 0) return false;
      const style = (n as HTMLElement).ownerDocument.defaultView?.getComputedStyle(n);
      if (!style) return true;
      return style.visibility !== 'hidden' && style.display !== 'none';
    };

    const container = findContainer(el);
    if (!container || container === document.body || container === document.documentElement) {
      out.filterState = null;
    } else {
      const lower = value.toLowerCase();
      const tooShort = lower.length < G.FILTER_MIN_QUERY_LEN;
      let visible = 0;
      let queryInResults = false;
      const items = container.querySelectorAll(G.RESULT_ITEM_SELECTOR);
      for (let i = 0; i < items.length && visible < G.FILTER_MAX_ITEMS; i++) {
        const item = items[i];
        if (item.contains(el)) continue;
        if (!isVisible(item)) continue;
        visible++;
        if (!queryInResults && !tooShort) {
          const text = (item.textContent || '').toLowerCase();
          if (text.includes(lower)) queryInResults = true;
        }
      }
      // Note on fallback-button discovery: the legacy code tried to
      // recover a button's aria-ref via `el.getAttribute('aria-ref')`,
      // which is always null in the main world. Without a ref-recovery
      // path, this attribute stayed null in every prod snapshot ever.
      // We keep the visibility/text scan above (it's free) but no
      // longer ship a `[filter-fallback=...]` attribute. If a real
      // need surfaces, it'd be a follow-up: a second `resolveRefs`
      // pass over candidate fallback DOM elements.
      void fallbackRe;
      out.filterState = {
        visible,
        queryInResults: tooShort ? false : queryInResults,
      };
    }
  }

  // ── modalV2 ────────────────────────────────────────────────────
  if (checks.has('modalV2')) {
    out.modalV2 = null;
    if (el.getAttribute('role') !== 'dialog' && el.getAttribute('role') !== 'alertdialog') {
      const className = typeof el.className === 'string' ? el.className : '';
      let framework = false;
      for (const sel of G.MODAL_FRAMEWORK_SELECTORS) {
        try {
          if (el.matches(sel)) {
            framework = true;
            break;
          }
        } catch {
          /* invalid selector — skip */
        }
      }
      let classMatch = false;
      if (!framework) {
        for (const pat of G.MODAL_CLASS_PATTERNS) {
          try {
            if (new RegExp(pat).test(className)) {
              classMatch = true;
              break;
            }
          } catch {
            /* skip */
          }
        }
      }
      if (framework || classMatch) {
        const style = window.getComputedStyle(el);
        const visible =
          style.display !== 'none' &&
          style.visibility !== 'hidden' &&
          !(el instanceof HTMLElement && el.offsetWidth === 0 && el.offsetHeight === 0);
        const hasInteractive =
          el.querySelector(
            'input, textarea, select, button, a[href], [role="button"], [role="link"], [role="textbox"], [role="combobox"]'
          ) !== null;
        if (visible && hasInteractive) {
          let augName: string | undefined;
          const heading = el.querySelector('h1, h2, h3, h4, h5, h6');
          if (heading?.textContent?.trim()) augName = heading.textContent.trim();
          out.modalV2 = {
            augRole: 'dialog',
            augName,
            cssTag: el.tagName.toLowerCase(),
            cssClasses: className.split(/\s+/).filter(Boolean),
            framework,
          };
        }
      }
    }
  }

  // ── dialogModality ─────────────────────────────────────────────
  // Verify a native dialog is actually rendered before the formatter
  // trusts its `aria-modal`. Standards-based: computed display/visibility
  // up the ancestor chain + rendered box on-screen. Conservative — only
  // reports `inert` on positive evidence the dialog is not visible, so a
  // genuine modal is never downgraded.
  if (checks.has('dialogModality')) {
    out.dialogModality = { inert: false };
    try {
      const isVisibleStyle = (n: Element): boolean => {
        const he = n as HTMLElement;
        if (he.offsetWidth === 0 && he.offsetHeight === 0 && he.getClientRects().length === 0) {
          return false;
        }
        const style = window.getComputedStyle(n);
        if (
          style.display === 'none' ||
          style.visibility === 'hidden' ||
          style.visibility === 'collapse'
        ) {
          return false;
        }
        if (parseFloat(style.opacity || '1') === 0) return false;
        return true;
      };
      // Self + ancestors: a hidden ancestor hides the dialog.
      let visible = true;
      let cur: Element | null = el;
      for (let i = 0; i < 25 && cur; i++) {
        if (!isVisibleStyle(cur)) {
          visible = false;
          break;
        }
        cur = cur.parentElement;
      }
      // On-screen: the dialog's box must intersect the viewport.
      let onScreen = true;
      if (visible) {
        const rect = el.getBoundingClientRect();
        const vw = window.innerWidth || document.documentElement.clientWidth || 0;
        const vh = window.innerHeight || document.documentElement.clientHeight || 0;
        if (rect.width === 0 || rect.height === 0) {
          onScreen = false;
        } else if (rect.right <= 0 || rect.bottom <= 0 || rect.left >= vw || rect.top >= vh) {
          onScreen = false;
        }
      }
      out.dialogModality = { inert: !(visible && onScreen) };
    } catch {
      out.dialogModality = null;
    }
  }

  // Shared svg → hint helper, inlined (page-world: no closures).
  const extractSvgHint = (root: Element): { svgHint?: string; lucideIconClass?: string } => {
    const svg = root.querySelector('svg');
    if (!svg) return {};
    let svgHint: string | undefined;
    const svgTitle = svg.querySelector('title');
    if (svgTitle?.textContent) svgHint = svgTitle.textContent.trim();
    if (!svgHint) svgHint = svg.getAttribute('aria-label') || undefined;
    if (!svgHint) {
      const use = svg.querySelector('use');
      const href = use?.getAttribute('href') || use?.getAttribute('xlink:href');
      if (href) {
        const hashIndex = href.indexOf('#');
        const fragment = hashIndex >= 0 ? href.slice(hashIndex + 1) : href;
        svgHint = fragment.replace(/^icon-/, '');
      }
    }
    if (!svgHint) {
      const dataIcon = svg.getAttribute('data-icon');
      if (dataIcon) svgHint = dataIcon;
    }
    let lucideIconClass: string | undefined;
    const cls = svg.getAttribute('class') || '';
    const m = cls.match(/\blucide-[a-z0-9-]+/i);
    if (m) lucideIconClass = m[0].toLowerCase();
    return { svgHint, lucideIconClass };
  };

  // Extract a meaningful icon-font class token from the element or a child
  // icon node — FontAwesome (`<i class="fa-solid fa-file-export">`),
  // Telerik/Kendo (`<svg class="k-svg-i-filter">`), or generic `icon-<name>`.
  // Returns the raw token (family prefix intact); the Node-side apply maps it
  // to a friendly label. Skips FA style/size tokens (`fa-solid`, `fa-lg`, …).
  // The generic `icon-<name>` family is only honored on dedicated child icon
  // tags (`<i>/<svg>/<em>`), never the control's own class, so utility classes
  // like `icon-button` can't masquerade as an icon name.
  const extractIconClass = (root: Element): string | undefined => {
    const FA_STYLE = /^fa-(solid|regular|light|thin|duotone|brands|sharp)$/;
    const FA_SIZE =
      /^fa-(2?xs|sm|lg|xl|2xl|fw|spin|pulse|border|inverse|stack(-[12]x)?|pull-left|pull-right|beat|fade|flip|shake|bounce|spin-pulse|rotate-[0-9a-z]+|flip-[a-z]+)$/;
    const classOf = (node: Element): string =>
      typeof node.className === 'string' ? node.className : node.getAttribute('class') || '';
    const pick = (raw: string, allowGeneric: boolean): string | undefined => {
      const tokens = raw.split(/\s+/).filter(Boolean);
      for (const t of tokens) {
        if (t.startsWith('fa-') && t !== 'fa' && !FA_STYLE.test(t) && !FA_SIZE.test(t)) return t;
      }
      for (const t of tokens) {
        if (t.startsWith('k-svg-i-') || t.startsWith('k-i-')) return t;
      }
      if (allowGeneric) {
        for (const t of tokens) if (/^icon-[a-z]/.test(t)) return t;
      }
      return undefined;
    };
    // Control's own class: only unambiguous icon families (a generic
    // `icon-…` here is usually a layout utility, not an icon name).
    const rootHit = pick(classOf(root), false);
    if (rootHit) return rootHit;
    // Dedicated child icon nodes. `querySelectorAll` is always present in a
    // real DOM; guard so a partial test double degrades gracefully.
    let kids: Element[] = [];
    try {
      kids = Array.from(root.querySelectorAll('i, svg, em, span'));
    } catch {
      kids = [];
    }
    for (const node of kids) {
      const tag = node.tagName;
      const hit = pick(classOf(node), tag === 'I' || tag === 'SVG' || tag === 'EM');
      if (hit) return hit;
    }
    return undefined;
  };

  // ── iconButton ────────────────────────────────────────────────
  if (checks.has('iconButton')) {
    try {
      const ariaLabel = el.getAttribute('aria-label') || undefined;
      const title = el.getAttribute('title') || undefined;
      const ariaHaspopup = el.getAttribute('aria-haspopup') || undefined;
      const ariaExpanded = el.getAttribute('aria-expanded') || undefined;
      const ariaControls = el.getAttribute('aria-controls') || undefined;
      const ariaOwns = el.getAttribute('aria-owns') || undefined;
      const ariaCurrent = el.getAttribute('aria-current') || undefined;

      let href: string | undefined;
      let pathname: string | undefined;
      if (el.tagName === 'A') {
        const anchor = el as HTMLAnchorElement;
        href = anchor.getAttribute('href') || undefined;
        pathname = anchor.pathname || undefined;
      }

      let navAncestorLabel: string | undefined;
      {
        let anc: Element | null = el.parentElement;
        for (let i = 0; i < 6 && anc; i++, anc = anc.parentElement) {
          if (anc.tagName === 'NAV' || anc.getAttribute('role') === 'navigation') {
            const l = anc.getAttribute('aria-label') || anc.getAttribute('aria-labelledby');
            if (l) {
              if (anc.hasAttribute('aria-labelledby')) {
                const target = document.getElementById(l);
                navAncestorLabel = target?.textContent?.trim() || undefined;
              } else {
                navAncestorLabel = l;
              }
            }
            break;
          }
        }
      }

      const { svgHint, lucideIconClass } = extractSvgHint(el);

      const srOnly = el.querySelector(
        '.sr-only, [class*="visually-hidden"], [class*="VisuallyHidden"]'
      );
      const srText = srOnly?.textContent?.trim() || undefined;

      const img = el.querySelector('img');
      const imgAlt = img instanceof HTMLImageElement ? img.alt || undefined : undefined;

      let reactComponentName: string | undefined;
      const fiberTarget = el.querySelector('svg') || el.firstElementChild;
      if (fiberTarget) {
        const fiberKey = Object.keys(fiberTarget).find((k) => k.startsWith('__reactFiber$'));
        if (fiberKey) {
          let fiber = (fiberTarget as unknown as Record<string, unknown>)[fiberKey] as
            | { type?: { displayName?: string; name?: string }; return?: unknown }
            | undefined;
          for (let i = 0; i < 10 && fiber; i++) {
            const name = fiber.type?.displayName || fiber.type?.name;
            if (
              name &&
              typeof name === 'string' &&
              !/^(svg|path|g|circle|rect|line|polyline|polygon|Fragment)$/i.test(name)
            ) {
              reactComponentName = name;
              break;
            }
            fiber = fiber.return as typeof fiber;
          }
        }
      }

      const iconClass = extractIconClass(el);

      out.iconButton = {
        ariaLabel,
        title,
        ariaHaspopup,
        ariaExpanded,
        ariaControls,
        ariaOwns,
        ariaCurrent,
        href,
        pathname,
        navAncestorLabel,
        svgHint,
        srText,
        imgAlt,
        reactComponentName,
        id: el.id || undefined,
        className: typeof el.className === 'string' ? el.className : undefined,
        lucideIconClass,
        iconClass,
      };
    } catch {
      out.iconButton = null;
    }
  }

  // ── genericClickable ──────────────────────────────────────────
  if (checks.has('genericClickable')) {
    try {
      const title = el.getAttribute('title') || undefined;
      const ariaLabel = el.getAttribute('aria-label') || undefined;
      const id = el.id || undefined;
      const className = typeof el.className === 'string' ? el.className : undefined;
      const { svgHint, lucideIconClass } = extractSvgHint(el);
      const iconClass = extractIconClass(el);
      const rawInnerText = el instanceof HTMLElement ? el.innerText : undefined;
      const innerText = rawInnerText?.replace(/\n+/g, ' ').trim().slice(0, 80) || undefined;
      out.genericClickable = {
        title,
        ariaLabel,
        svgHint,
        id,
        className,
        innerText,
        lucideIconClass,
        iconClass,
      };
    } catch {
      out.genericClickable = null;
    }
  }

  // ── clickableImg ──────────────────────────────────────────────
  if (checks.has('clickableImg')) {
    try {
      let alt: string | undefined;
      if (el instanceof HTMLImageElement && el.alt) alt = el.alt;
      const title = el.getAttribute('title') || undefined;
      const ariaLabel = el.getAttribute('aria-label') || undefined;
      let src: string | undefined;
      if (el instanceof HTMLImageElement && el.src && !el.src.startsWith('data:')) {
        // Signed URLs (S3 GetObject, GCS, etc.) can be 1KB+ — keeping the
        // raw value bloats the substrate. Drop query+fragment first; if
        // still long, keep only the last path segment. The locator's
        // `imgSrc` predicate anchors on a filename substring anyway, so
        // truncation doesn't hurt locator quality. `data:` URLs are
        // skipped entirely (inline base64 — never a stable selector).
        let raw = el.src.split('?')[0].split('#')[0];
        if (raw.length > 200) {
          const slash = raw.lastIndexOf('/');
          raw = slash >= 0 ? raw.slice(slash + 1) : raw.slice(-200);
        }
        src = raw;
      }
      out.clickableImg = { alt, title, ariaLabel, src };
    } catch {
      out.clickableImg = null;
    }
  }

  // ── toggle ────────────────────────────────────────────────────
  if (checks.has('toggle')) {
    try {
      let label: string | undefined;
      label = el.getAttribute('aria-label') || undefined;
      if (!label) label = el.getAttribute('title') || undefined;

      if (!label && el.tagName === 'INPUT') {
        const input = el as HTMLInputElement;
        const ls = input.labels;
        if (ls && ls.length > 0) {
          const t = ls[0].textContent?.trim();
          if (t) label = t;
        }
      }

      if (!label) {
        const ledBy = el.getAttribute('aria-labelledby');
        if (ledBy) {
          const target = document.getElementById(ledBy);
          const t = target?.textContent?.trim();
          if (t) label = t;
        }
      }

      let checked: boolean | undefined;
      const ariaChecked = el.getAttribute('aria-checked');
      if (ariaChecked === 'true' || ariaChecked === 'false') {
        checked = ariaChecked === 'true';
      } else if (el.tagName === 'INPUT') {
        checked = (el as HTMLInputElement).checked;
      }

      let disabled: boolean | undefined;
      const ariaDisabled = el.getAttribute('aria-disabled');
      if (ariaDisabled === 'true') disabled = true;
      else if (el.tagName === 'INPUT' && (el as HTMLInputElement).disabled) disabled = true;

      out.toggle = { label, checked, disabled };
    } catch {
      out.toggle = null;
    }
  }

  // ── testId ────────────────────────────────────────────────────
  if (checks.has('testId')) {
    try {
      const raw =
        el.getAttribute('data-testid') ||
        el.getAttribute('data-test-id') ||
        el.getAttribute('data-test') ||
        undefined;
      out.testId = raw && raw.length <= 120 ? raw : null;
    } catch {
      out.testId = null;
    }
  }

  // ── dataId ─────────────────────────────────────────────────────
  if (checks.has('dataId')) {
    try {
      // Entity `data-id` on rows/cards/list items/panels. Captured raw — the
      // locator decides stability via the shared classifier.
      const dataId = el.getAttribute('data-id') || undefined;
      out.dataId = dataId && dataId.length <= 120 ? dataId : null;
    } catch {
      out.dataId = null;
    }
  }

  // ── dataField ──────────────────────────────────────────────────
  if (checks.has('dataField')) {
    out.dataField = null;
    try {
      const field = el.getAttribute('data-field');
      // MUI DataGrid internal columns carry framework-reserved field names
      // (selection checkbox, row number, reorder handle, etc.) that are not
      // meaningful, stable locator anchors.
      const reserved =
        field === '__check__' ||
        field === '__row_number__' ||
        field === '__reorder__' ||
        field === '__detail_panel_toggle__' ||
        field === '__tree_data_group__';
      if (field && !reserved) {
        const cls = typeof el.className === 'string' ? el.className : '';
        const editable =
          /(?:^|[\s-])editable(?:[\s-]|$)/i.test(cls) ||
          el.getAttribute('aria-readonly') === 'false' ||
          el.querySelector('input, textarea, select, [contenteditable="true"]') !== null;
        out.dataField = { field: field.slice(0, 120), editable };
      }
    } catch {
      out.dataField = null;
    }
  }

  // ── svgPromote ────────────────────────────────────────────────
  if (checks.has('svgPromote')) {
    try {
      out.svgPromote = el.hasAttribute('data-svg-enriched');
    } catch {
      out.svgPromote = false;
    }
  }

  return out;
}

// ─── Apply phase ────────────────────────────────────────────────────────

const REF_LINE_RE = /\[ref=([^\]\s]+)\]/;

/**
 * Build a `Map<ref, lineIndex>` over the yaml so the apply phase can
 * locate every ref line in O(1) and mutate the line array in place,
 * instead of split/joining the whole yaml per (ref × edit).
 */
function indexRefs(lines: string[]): Map<string, number> {
  const idx = new Map<string, number>();
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (!line.includes('[ref=')) continue;
    if (!line.trimStart().startsWith('- ')) continue;
    const m = REF_LINE_RE.exec(line);
    if (m && !idx.has(m[1])) idx.set(m[1], i);
  }
  return idx;
}

/**
 * Insert ` [key=value]` into a line, BEFORE any trailing children
 * marker (`:`) or value tail (`: value`). Idempotent — already-present
 * attrs are untouched.
 *
 * Lines look like:
 *   - role "name" [attr1] [ref=eN]              ← bare
 *   - role "name" [attr1] [ref=eN]:             ← children marker
 *   - role [ref=eN]: value text                 ← value tail
 *
 * The new attr always lands inside the `[ref=eN]` block, never after
 * a value tail (which would otherwise corrupt the value).
 */
function appendAttributeIfMissing(line: string, key: string, value: string): string {
  if (!value) return line;
  if (line.includes(`[${key}=`) || line.includes(`[${key}]`)) return line;
  const segment = ` [${key}=${value}]`;
  // Match through the LAST `]` before any `:` value/children marker.
  // Greedy `.*` to find the rightmost `]` that's not inside the value
  // tail (yaml values can contain `:` but not `]`-followed-by-`:`).
  const m = line.match(/^(.*\])(\s*:.*)?$/);
  if (m && m[2]) return `${m[1]}${segment}${m[2]}`;
  return `${line}${segment}`;
}

/**
 * Append `: <value>` to a line that has no value tail. Idempotent —
 * a line with an existing `:` (children marker or value) is untouched.
 */
function appendValueTail(line: string, value: string): string {
  if (/\]\s*:/.test(line)) return line;
  return `${line}: ${value}`;
}

/**
 * Inject `"<name>"` after the line's role keyword, mirroring the
 * shape Playwright emits for fields that carry an accessible name:
 *
 *   - textbox [ref=e3]              →  - textbox "Email" [ref=e3]
 *   - textbox [ref=e3]: hello       →  - textbox "Email" [ref=e3]: hello
 *
 * Only fires when the line has no quoted name yet. Lines that already
 * carry a quoted role-name (Playwright surfaced it) are returned as-is.
 */
function injectQuotedRoleName(line: string, name: string): string {
  // Find the leading `- <role>` token. If the role is already followed
  // by a quoted name, do nothing.
  const m = line.match(/^(\s*-\s+[\w-]+)(\s+"[^"]*")?(.*)$/);
  if (!m) return line;
  if (m[2]) return line; // already has a quoted name
  const escaped = name.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  return `${m[1]} "${escaped}"${m[3]}`;
}

/**
 * Like `injectQuotedRoleName` but ALSO replaces an existing quoted
 * label when the line already has one (used by icon/generic
 * augmentation when the substrate glyph is a private-use placeholder
 * we want to overwrite with a friendly label).
 */
function injectElementLabel(line: string, role: string, label: string): string {
  const escaped = label.replace(/"/g, '\\"');
  const quotedPattern = new RegExp(`(-\\s+${role}\\s+)"[^"]*"`);
  if (quotedPattern.test(line)) {
    return line.replace(quotedPattern, `$1"${escaped}"`);
  }
  const unlabeledPattern = new RegExp(`(-\\s+${role})(\\s*(?:\\[|:|$))`);
  if (unlabeledPattern.test(line)) {
    return line.replace(unlabeledPattern, `$1 "${escaped}"$2`);
  }
  return line;
}

/**
 * Replace a line's existing role keyword with a new role (e.g.
 * `generic` → `svg-shape`). No-op when the role doesn't match.
 */
function replaceRoleOnLine(line: string, fromRole: string, toRole: string): string {
  const pattern = new RegExp(`^(\\s*-\\s+)${fromRole}(\\s|$)`);
  if (!pattern.test(line)) return line;
  return line.replace(pattern, `$1${toRole}$2`);
}

/**
 * Walk the parsed snapshot tree to find a context label for a target
 * ref by looking at preceding-sibling text in each ancestor. Used by
 * the toggle augmenter so a row's switch can borrow the row's identity
 * (first gridcell text) when it has no aria-label.
 */
const TOGGLE_CONTEXT_SKIP_ROLES = new Set([
  'rowheader',
  'columnheader',
  'checkbox',
  'radio',
  'switch',
  'button',
  ...FORM_FIELD_ROLES,
]);

function findContextLabelForRef(
  elements: SnapshotElement[],
  targetRef: string
): string | undefined {
  const chain: Array<{ parent: SnapshotElement; idx: number }> = [];
  const build = (els: SnapshotElement[], parent: SnapshotElement | null): boolean => {
    for (let i = 0; i < els.length; i++) {
      const el = els[i];
      if (el.ref === targetRef) {
        if (parent) chain.push({ parent, idx: i });
        return true;
      }
      if (build(el.children, el)) {
        const idx = els.indexOf(el);
        if (parent) chain.push({ parent, idx });
        return true;
      }
    }
    return false;
  };
  build(elements, null);
  const hasAlpha = (s: string) => /[a-zA-Z]/.test(s);
  for (const { parent, idx } of chain) {
    let fallback: string | undefined;
    for (let i = 0; i < idx; i++) {
      const sib = parent.children[i];
      if (TOGGLE_CONTEXT_SKIP_ROLES.has(sib.role)) continue;
      const t = deepestTextSkipping(sib, TOGGLE_CONTEXT_SKIP_ROLES);
      if (!t || t.length === 0 || t.length >= 80 || !/[a-zA-Z0-9]/.test(t)) continue;
      if (hasAlpha(t)) return t;
      fallback = t;
    }
    if (fallback) return fallback;
  }
  return undefined;
}

function deepestTextSkipping(el: SnapshotElement, skipRoles: Set<string>): string | undefined {
  if (skipRoles.has(el.role)) return undefined;
  const isContainer =
    el.role === 'gridcell' ||
    el.role === 'row' ||
    el.role === 'rowgroup' ||
    el.role === 'generic' ||
    el.role === 'cell';
  const own = el.text?.trim() || el.inputValue?.trim();
  if (own && !isContainer) return own;
  for (const c of el.children) {
    const t = deepestTextSkipping(c, skipRoles);
    if (t) return t;
  }
  if (own) return own;
  return undefined;
}

/**
 * Derive a human-readable label from a URL pathname. Used by the
 * anchor-link pre-pass (no DOM lookup needed — the substrate already
 * carries `url` for every link). Same logic as the legacy
 * `labelFromPathname` in snapshot-augmentation.ts.
 */
function labelFromPathname(pathname: string | undefined): string | undefined {
  if (!pathname) return undefined;
  const segments = pathname.split(/[?#]/)[0].split('/').filter(Boolean);
  if (segments.length === 0) return 'Home';
  const meaningful = [...segments];
  while (meaningful.length > 1) {
    const last = meaningful[meaningful.length - 1];
    if (/^[0-9a-f-]{8,}$/i.test(last) || /^\d+$/.test(last)) {
      meaningful.pop();
    } else {
      break;
    }
  }
  const tail = meaningful[meaningful.length - 1];
  if (!tail) return undefined;
  if (/^[0-9a-f-]{8,}$/i.test(tail) || /^\d+$/.test(tail)) return undefined;
  const words = tail.split(/[-_]/).filter(Boolean);
  if (words.length === 0) return undefined;
  const titled = words.map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
  return titled.length > 0 && titled.length < 40 ? titled : undefined;
}

// ─── Orchestrator ───────────────────────────────────────────────────────

export async function runPerRefAugmentations(
  scope: Page | Frame,
  yaml: string,
  logger: BrowserLogger,
  opts: PerRefAugmentationOpts = {}
): Promise<PerRefAugmentationResult> {
  const result: PerRefAugmentationResult = { yaml, v2ModalOverrides: [] };
  const passStartedAt = Date.now();
  let workItemCount = 0;
  let planDurationMs = 0;
  let resolveDurationMs = 0;
  let resolveDiagnostics: { requested: number; resolved: number; succeeded: number } | null = null;

  let phase: 'parse' | 'plan' | 'resolve' | 'apply' = 'parse';
  try {
    throwIfAborted(opts.signal);
    const elements = opts.preParsedElements ?? parseSnapshot(yaml);
    throwIfAborted(opts.signal);
    if (elements.length === 0) return result;

    phase = 'plan';
    const planStartedAt = Date.now();
    const workItems = planPerRefChecks(elements, opts);
    planDurationMs = Date.now() - planStartedAt;
    workItemCount = workItems.length;
    throwIfAborted(opts.signal);
    if (workItems.length === 0) return result;

    phase = 'resolve';
    const globals: PageGlobals = {
      RESULT_ITEM_SELECTOR,
      FILTER_FALLBACK_PATTERN: FILTER_FALLBACK_TEXT_RE_SOURCE,
      FILTER_MAX_ITEMS: FILTER_MAX_RESULT_ITEMS,
      FILTER_MIN_QUERY_LEN: FILTER_QUERY_MIN_LEN,
      FILTER_ANCESTOR_DEPTH: FILTER_ANCESTOR_WALK_DEPTH,
      HCLIP_ANCESTOR_DEPTH: HCLIP_ANCESTOR_WALK_DEPTH,
      MODAL_FRAMEWORK_SELECTORS: MODAL_V2_FRAMEWORK_SELECTORS,
      MODAL_CLASS_PATTERNS: MODAL_V2_CLASS_PATTERNS,
    };
    const resolveStartedAt = Date.now();
    const { results: perRef, diagnostics } = await resolveRefsWithArgs(
      scope,
      workItems,
      globals,
      pageSideFn,
      { signal: opts.signal }
    );
    resolveDurationMs = Date.now() - resolveStartedAt;
    resolveDiagnostics = diagnostics;
    throwIfAborted(opts.signal);

    // Mass-null detection: original silent-noop bug presented as
    // "every check returned null because every CSS query missed."
    // Catch that fingerprint at warn-level so a future regression
    // doesn't hide for years.
    if (
      diagnostics.resolved > 0 &&
      diagnostics.succeeded / diagnostics.resolved < 1 - MASS_NULL_RATE
    ) {
      logger.warn('[PerRefAugmentation] high null rate from page-side dispatcher', {
        ...diagnostics,
        v2: !!opts.v2,
      });
    }

    phase = 'apply';
    const lines = yaml.split('\n');
    const lineIndex = indexRefs(lines);
    const modalCandidates: Array<{
      ref: string;
      override: V2ModalOverride;
      framework: boolean;
    }> = [];

    for (const it of workItems) {
      const r = perRef[it.ref];
      if (!r) continue;
      const i = lineIndex.get(it.ref);
      if (i === undefined) continue;

      if (opts.v2 && r.testId) {
        lines[i] = appendAttributeIfMissing(lines[i], '~test-id', encodeV2Value(r.testId));
      }

      if (opts.v2 && r.dataId) {
        lines[i] = appendAttributeIfMissing(lines[i], '~data-id', encodeV2Value(r.dataId));
      }

      // dataField — emit the grid cell's `data-field` as a row-scoped locator
      // anchor, plus an `~editable` flag so the formatter surfaces empty
      // editable cells. v2-only (matches the planner gate).
      if (opts.v2 && r.dataField) {
        lines[i] = appendAttributeIfMissing(lines[i], '~field', encodeV2Value(r.dataField.field));
        if (r.dataField.editable) {
          lines[i] = appendAttributeIfMissing(lines[i], '~editable', 'true');
        }
      }

      // editability
      if (r.editability) {
        if (r.editability.readonly) {
          lines[i] = appendAttributeIfMissing(lines[i], 'readonly', 'true');
        }
        if (r.editability.disabled) {
          lines[i] = appendAttributeIfMissing(lines[i], 'disabled', 'true');
        }
      }

      // hclip — horizontally-clipped target. Stamped as a `~`-attr so the
      // parser ignores it (default case) and the formatter strips it; the
      // executor reads it from the raw substrate to emit `[scroll-left|right]`.
      if (r.hclip) {
        lines[i] = appendAttributeIfMissing(lines[i], '~hclip', r.hclip);
      }

      // dialogModality — an inert dialog (collapsed/off-screen, e.g. a chat
      // widget that left `aria-modal=true` on) must not demote the page to
      // background or crowd the DIALOG STACK. Stamp a `~`-attr the formatter
      // reads in buildSection; absent attr = unchanged (treat as blocking).
      if (r.dialogModality?.inert) {
        lines[i] = appendAttributeIfMissing(lines[i], '~dialog-inert', 'true');
      }

      // gridCellValue runs first; formFieldLabel below skips when a
      // colon-tail is now present. Avoids the double-emit footgun
      // (`: Acme [value=Email]`) for fields that are both gridcell-
      // nested AND have a `<label>` association.
      if (r.gridCellValue) {
        const escaped = r.gridCellValue.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
        lines[i] = appendValueTail(lines[i], escaped);
      }

      // formFieldLabel — inject as the line's quoted accessible name
      // (e.g. `- textbox "Email" [ref=eN]`), the same shape Playwright
      // emits for fields with explicit labels. Using `[value=…]` here
      // would overload with input-value semantics and confuse agents.
      // Skips when the line already carries the same quoted name.
      if (r.formFieldLabel) {
        const safe = r.formFieldLabel.slice(0, 80);
        const line = lines[i];
        if (!line.includes(`"${safe}"`)) {
          lines[i] = injectQuotedRoleName(line, safe);
        }
      }

      // filterState
      if (r.filterState) {
        const s = r.filterState;
        let line = lines[i];
        line = appendAttributeIfMissing(line, 'filter-visible', String(s.visible));
        line = appendAttributeIfMissing(
          line,
          'filter-query-in-results',
          s.queryInResults ? 'true' : 'false'
        );
        lines[i] = line;
      }

      // modalV2 — collect; cap+order applied below.
      if (r.modalV2) {
        modalCandidates.push({
          ref: it.ref,
          override: {
            ref: it.ref,
            augRole: r.modalV2.augRole,
            augName: r.modalV2.augName,
            cssTag: r.modalV2.cssTag,
            cssClasses: r.modalV2.cssClasses,
          },
          framework: r.modalV2.framework,
        });
      }

      // Tag the line with `~name-src=augmented:dom-mutation` so
      // `buildLocator`'s source-durability gate refuses to lean on
      // these synthesized names. Only meaningful in v2 mode; v1 path
      // ignores the attr and behaves identically.
      const tagAugmentedNameSource = (lineIdx: number) => {
        if (!opts.v2) return;
        lines[lineIdx] = appendAttributeIfMissing(
          lines[lineIdx],
          '~name-src',
          'augmented:dom-mutation'
        );
      };

      // iconButton — pick the strongest hint from a fixed-priority chain.
      // The substrate quoted name lands as the visible accessible label;
      // raw class signals (`~icon-class`, `~semantic-class`) are emitted
      // as separate attributes so locator predicates can anchor on them.
      if (r.iconButton) {
        const h = r.iconButton;
        const linePreLabel = lines[i];
        const role: 'button' | 'link' = linePreLabel.includes('- link') ? 'link' : 'button';
        const pathnameLabel = role === 'link' ? labelFromPathname(h.pathname) : undefined;
        const hint =
          h.title ||
          h.ariaLabel ||
          h.svgHint ||
          h.srText ||
          h.imgAlt ||
          (h.iconClass ? resolveIconClassName(h.iconClass) : undefined) ||
          pathnameLabel ||
          h.reactComponentName ||
          h.id ||
          undefined ||
          extractSemanticHint(h.className);
        if (hint) {
          lines[i] = injectElementLabel(lines[i], role, hint);
          tagAugmentedNameSource(i);
        }
        let line = lines[i];
        if (h.title) line = appendAttributeIfMissing(line, 'title', h.title);
        if (h.ariaHaspopup) line = appendAttributeIfMissing(line, 'aria-haspopup', h.ariaHaspopup);
        if (h.ariaExpanded) line = appendAttributeIfMissing(line, 'aria-expanded', h.ariaExpanded);
        if (h.ariaControls) line = appendAttributeIfMissing(line, 'aria-controls', h.ariaControls);
        if (h.ariaOwns) line = appendAttributeIfMissing(line, 'aria-owns', h.ariaOwns);
        if (h.ariaCurrent) line = appendAttributeIfMissing(line, 'aria-current', h.ariaCurrent);
        if (h.lucideIconClass) {
          line = appendAttributeIfMissing(line, '~icon-class', h.lucideIconClass);
        } else if (h.iconClass) {
          line = appendAttributeIfMissing(line, '~icon-class', h.iconClass);
        }
        const semanticToken = extractSemanticHint(h.className);
        if (semanticToken) {
          line = appendAttributeIfMissing(line, '~semantic-class', semanticToken);
        }
        if (role === 'link' && h.href) {
          line = appendAttributeIfMissing(line, 'href', h.href);
        }
        if (h.navAncestorLabel) {
          line = appendAttributeIfMissing(line, 'nav', h.navAncestorLabel);
        }
        lines[i] = line;
      }

      // genericClickable — `<div role="button">`-style affordances.
      if (r.genericClickable) {
        const h = r.genericClickable;
        const hint =
          h.title ||
          h.ariaLabel ||
          h.innerText ||
          h.svgHint ||
          (h.iconClass ? resolveIconClassName(h.iconClass) : undefined) ||
          h.id ||
          undefined ||
          extractSemanticHint(h.className);
        if (hint) {
          lines[i] = injectElementLabel(lines[i], 'generic', hint);
          tagAugmentedNameSource(i);
        }
        let line = lines[i];
        if (h.lucideIconClass) {
          line = appendAttributeIfMissing(line, '~icon-class', h.lucideIconClass);
        } else if (h.iconClass) {
          line = appendAttributeIfMissing(line, '~icon-class', h.iconClass);
        }
        const semanticToken = extractSemanticHint(h.className);
        if (semanticToken) {
          line = appendAttributeIfMissing(line, '~semantic-class', semanticToken);
        }
        lines[i] = line;
      }

      // clickableImg — alt/title/aria-label win as the visible label;
      // raw `src` flows onto the substrate so render derives a friendly
      // filename and `buildLocator`'s `imgSrc` predicate has CSS anchor.
      if (r.clickableImg) {
        const h = r.clickableImg;
        const label = h.alt || h.title || h.ariaLabel;
        if (label) {
          lines[i] = injectElementLabel(lines[i], 'img', label);
          tagAugmentedNameSource(i);
        }
        if (h.src) {
          lines[i] = appendAttributeIfMissing(lines[i], 'src', h.src);
        }
      }

      // toggle — synthesize a label from DOM context when missing,
      // and surface checked/disabled state as attributes.
      if (r.toggle) {
        const h = r.toggle;
        const role = lines[i].includes('- switch') ? 'switch' : 'checkbox';
        const lineHasText = /-\s+(switch|checkbox)\s+"[^"]+"/.test(lines[i]);
        const label =
          h.label || (lineHasText ? undefined : findContextLabelForRef(elements, it.ref));
        if (!lineHasText && label) {
          lines[i] = injectElementLabel(lines[i], role, label);
          tagAugmentedNameSource(i);
        }
        let line = lines[i];
        if (typeof h.checked === 'boolean') {
          line = appendAttributeIfMissing(line, 'checked', String(h.checked));
        }
        if (h.disabled) {
          line = appendAttributeIfMissing(line, 'disabled', 'true');
        }
        lines[i] = line;
      }

      // svgPromote — generic-role nodes that pre-snapshot SVG enrichment
      // tagged with `data-svg-enriched` get role-promoted to `svg-shape`.
      if (r.svgPromote === true) {
        lines[i] = replaceRoleOnLine(lines[i], 'generic', 'svg-shape');
      }
    }

    if (modalCandidates.length > 0) {
      modalCandidates.sort((a, b) => Number(b.framework) - Number(a.framework));
      result.v2ModalOverrides = modalCandidates
        .slice(0, MODAL_V2_MAX_ENRICHMENTS)
        .map((c) => c.override);
    }

    result.yaml = lines.join('\n');
    const totalDurationMs = Date.now() - passStartedAt;
    if (workItemCount >= LARGE_PASS_LOG_THRESHOLD || totalDurationMs >= 1_000) {
      logger.debug('[PerRefAugmentation] large pass completed', {
        workItemCount,
        batchCount: Math.ceil(workItemCount / DEFAULT_REF_RESOLUTION_BATCH_SIZE),
        planDurationMs,
        resolveDurationMs,
        totalDurationMs,
        ...(resolveDiagnostics ?? {}),
        v2: !!opts.v2,
      });
    }
    return result;
  } catch (err) {
    if (opts.signal?.aborted) return result;
    // Bumped from debug → warn: substrate-augmentation failures
    // were previously invisible in prod; this is the bug class that
    // motivated the consolidation, so make the regression mode
    // observable.
    logger.warn('[PerRefAugmentation] pass failed, returning input yaml', {
      phase,
      v2: !!opts.v2,
      error: errorMessage(err),
    });
    return result;
  }
}
